<template>
  <div
     class="iconica-sidebar-link"
    :class="[`theme-${theme}`, { 'no-hover': noHover }, { 'is-dsbld': disabled, 'is-fix-picture-transparency': fixPictureTransparency && picture }]"
    :title="title"
    :style="style.main"
    @click="handleClick">
    <span v-if="text && !icon" class="txt">
      <span>{{ text | first-two-upper }}</span>
    </span>
    <span v-if="icon" class="icon">
      <i :class="icon" :style="style.icon"></i>
    </span>
    <span v-if="picture" class="picture" :style="style.picture"></span>
    <span class="circle" :style="style.circle"></span>
  </div>
</template>

<script>
export default {
  name: 'nuttrema-iconica-sidebar-link',
  props: {
    size: {
      type: String,
      required: false,
      default: 'normal',
    },
    theme: {
      type: String,
      required: false,
      default: 'default',
    },
    text: {
      type: String,
      required: false,
      default: '',
    },
    picture: {
      type: String,
      required: false,
      default: '',
    },
    icon: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    noHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    fixPictureTransparency: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      sizes: {
        small: {
          width: '1.85em',
          fontSize: '1.15rem',
          margin: '0.5em',
          circle: {
            width: '3.5em',
            top: '-0.5em',
            left: '-0.5em',
          },
        },
        normal: {
          width: '2.5em',
          fontSize: '1.3rem',
          margin: '0.5em',
          circle: {
            width: '3.5em',
            top: '-0.5em',
            left: '-0.5em',
          },
        },
        picture: {
          width: '2.5em',
          fontSize: '1.3rem',
          margin: '0',
          circle: {
            width: '3.5em',
            top: '-0.5em',
            left: '-0.5em',
          },
        },
        large: {
          width: '3em',
          fontSize: '1.4rem',
          margin: '0.5em',
          circle: {
            width: '3.5em',
            top: '-0.5em',
            left: '-0.5em',
          },
        },
      },
    };
  },
  computed: {
    style() {
      const size = this.sizes[this.size];
      return {
        main: `width: ${size.width}; max-width: ${size.width}; height: ${size.width}; max-height: ${size.width}; margin: ${size.margin}`,
        icon: `font-size: ${size.fontSize};`,
        picture: `background-image: url(${this.picture})`,
        circle: `width: ${size.circle.width}; max-width: ${size.circle.width}; height: ${size.circle.width}; max-height: ${size.circle.width}; left: ${size.circle.left}; top: ${size.circle.top}`,
      };
    },
  },
  methods: {
    handleClick(evt) {
      this.$emit('click', evt);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~styles/mixins/mixins";
  @import "~styles/mixins/gradients";
  @import "~styles/theme/colors";
  @import "~styles/theme/content-colors";
  @import "~styles/theme/person-colors";

  .iconica-sidebar-link {
    display: block;
    position: relative;
    border-radius: 50%;
    background-color: darken($turquoise, 5%);
    span.txt,
    span.icon {
        display: table;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0; left: 0;
        vertical-align: middle;
        text-align: center;
        z-index: 1;
        color: #fff;
        > span, > i {
            display: table-cell;
            vertical-align: middle;
            font-size: 1.25rem;
        }
        > i {
            font-size: 24px;
        }
    }

    span.picture {
      display: inline-block;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0; left: 0;
      border-radius: 50%;
      background-image: none;
      background-size: 100% auto;
      background-position: center center;
      background-repeat: no-repeat;
      z-index: 2;
      @include insetShadow(rgba(0, 0, 0, 0.75), 0px 3px 3px -4px);
    }

    span.txt {
      > span {
        font-size: 1rem;
        font-weight: 600;
        text-shadow: 0 -1px 0 rgba(0,0,0,0.35);
      }
    }

    span.circle {
      z-index: 3;
      opacity: 0;
      content: '';
      border-radius: 50%;
      position: absolute;
      display: inline-block;
      transition: all 0.5s linear;
      -o-transition: all 0.5s linear;
      -moz-transition: all 0.5s linear;
      -webkit-transition: all 0.5s linear;
      border: 4px solid darken($turquoise, 5%);
    }

    &:hover, &:active, &:focus {
      cursor: pointer;
      span.circle {
        opacity: 1;
      }
    }

    &.no-hover {
      &:hover, &:active, &:focus {
        cursor: default;
        span.circle { opacity: 0; }
      }
    }

    &.is-dsbld {
      background-color: #607d8b;
      &:hover, &:active, &:focus {
        cursor: pointer;
        background-color: darken(#607d8b, 10%);
        span.circle { opacity: 0; }
      }
    }

    .is-active & {
      background-color: darken($turquoise, 10%);
      span.circle {
        opacity: 1;
        border-color: darken($turquoise, 10%);
      }
    }

    .nuttrema-theme-professional-topography & {
      $color: #6b7786;
      $border-color: #00BCD4;
      background-color: $color;

      span.txt, span.icon {
        color: #fff;
      }

      span.circle {
        border-color: $border-color;
      }

      &:hover, &:focus {
        background-color: darken($color, 5%);
        span.circle {
          border-color: lighten($border-color, 5%);
        }
      }

      &.is-dsbld {
        background-color: #cbd2d7;
        span.icon {
          // color: #bcc3c8;
        }
      }

      .is-active & {
        background-color: darken($color, 10%);
      }
    }

    &.theme-gray {
      $color: #c8d0e1;
      background-color: darken($color, 10%);
      span.txt, span.icon {
        color: #fff;
      }
      span.circle {
        border-color: darken($color, 10%);
      }
      &:hover, &:focus {
        span.circle {
          border-color: darken($color, 15%);
        }
      }
      &.is-active {
        background-color: darken($color, 15%);
        span.circle {
          opacity: 1;
          border-color: darken($color, 15%);
        }
      }
    }

    &.theme-white {
      $color: #fff;
      background-color: $color;
      span.txt, span.icon {
        color: invert($color);
      }
      span.circle {
        border-color: darken($color, 10%);
      }
      &:hover, &:focus {
        background-color: $color;
        span.circle {
          border-color: $color;
        }
      }
    }

    &.theme-thin-white {
      $color: #fff;
      transition: all 0.25s;
      background-color: transparent;
      span.txt, span.icon {
        color: #fff;
      }
      span.circle {
        border-color: transparent;
      }
      .is-active &, &:hover, &:active, &:focus {
        background-color: #fff;
        span.txt, span.icon {
          color: #000;
        }
        span.circle {
          border-color: rgba(#fff, 0.25);
        }
      }
    }

    &.theme-gallery {
      $color: #fff;
      transition: all 0.25s;
      background-color: transparent;

      span.txt, span.icon {
        color: #fff;
      }

      span.circle {
        border-color: transparent;
      }

      .is-active & {
        background-color: transparent;
        span.txt, span.icon {
        color: #fff;
        }
        span.circle {
          border-color: transparent;
        }
      }

      &:hover, &:active, &:focus {
        background-color: #fff;
        span.txt, span.icon {
          color: #000;
        }
        span.circle {
          border-color: rgba(#fff, 0.25);
        }
      }
    }

    @mixin applyTheme($bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg) {
      background-color: $bg;
      span.txt, span.icon {
        color: $text;
      }
      span.circle {
        opacity: 1;
        border-color: $border;
      }
      .is-active &, &:hover, &:focus {
        background-color: $focus-bg;
        span.circle {
          border-color: $focus-border;
        }
      }
      &.no-hover {
        background-color: $no-hover-bg;
        span.circle { opacity: 0; }
        &:hover, &:active, &:focus {
          cursor: default;
          span.circle { opacity: 0; }
        }
      }
    }

    &.theme-female {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($female-dark, #fff, $female-lighter, $female-darker, $female, $female-dark);
    }

    &.theme-male {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($male-dark, #fff, $male-lighter, $male-darker, $male, $male-dark);
    }

    &.theme-genderless {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($genderless, #fff, $genderless-lighter, $genderless-darker, $genderless, $genderless-dark);
    }

    &.theme-instance_admin {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($admin, #fff, $admin-lighter, $admin-darker, $admin, $admin-dark);
    }

    &.theme-instance_owner {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($owner, #fff, $owner-lighter, $owner-darker, $owner, $owner-dark);
    }

    &.theme-food-product {
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($color-food-product, #fff, $color-food-product-light, $color-food-product-dark, $color-food-product, $color-food-product);
    }

    &.theme-dish {
      @include applyTheme($color-dish, #fff, $color-dish-light, $color-dish-dark, $color-dish, $color-dish);
    }

    &.theme-drink {
      @include applyTheme($color-drink, #fff, $color-drink-light, $color-drink-dark, $color-drink, $color-drink);
    }

    &.theme-meal {
      @include applyTheme($color-meal, #fff, $color-meal-light, $color-meal-dark, $color-meal, $color-meal);
      span.icon i {
        font-size: 1.5rem !important;
      }
    }

    &.theme-purchase {
      @include applyTheme(#f7ba2a, #606266, transparent, darken(#f7ba2a, 5%), darken(#f7ba2a, 5%), transparent);
      .is-active &, &:hover, &:focus {
        span.txt, span.icon {
          color: #303133;
        }
      }
    }

    &.theme-diet {
      background-color: $color-diet-light;
      span.txt, span.icon {
        color: #fff;
      }
      span.circle {
        border-color: $color-diet;
      }
      .is-active &, &:hover, &:focus {
        background-color: $color-diet;
        span.circle {
          border-color: $color-diet-dark;
        }
      }
    }

    &.theme-instance {
      $no-plan-color: #9e9e9e;
      $standard-color: #409EFF;
      $trial-color: #e3ac33;

      @mixin style($bg-color, $text-color: #fff) {
        background-color: $bg-color;
        span.txt, span.icon {
          color: $text-color;
        }
        span.circle {
          border-color: $bg-color;
          opacity: 1;
        }
        .is-active &, &:hover, &:focus {
          background-color: $bg-color;
          span.circle {
            border-color: darken($bg-color, 5%);
          }
        }
      }

      &.is-no-plan {
        @include style($no-plan-color);
      }

      &.is-standard {
        @include style($standard-color);
      }

      &.is-trial {
        @include style($trial-color);
      }
    }

    &.theme-instruction-step {
      background-color: $color-instruction-step;
      span.txt {
        color: #fff;
        > span {
          line-height: 1;
          text-shadow: none;
          font-size: 1.5rem;
          color: rgb(255, 255, 255);
        }
      }
      span.circle {
        border-color: darken($color-instruction-step, 10%);
        // opacity: 1;
      }
      .is-active &, &:hover, &:focus {
        background-color: darken($color-instruction-step, 5%);
        span.circle {
          border-color: darken($color-instruction-step, 10%);
        }
      }
    }

    &.theme-picture-white-border {
      span.picture {
        border: 2px solid #fff;
      }
    }

    &.theme-add-button {
      $color: #00BCD4;
      // $bg, $text, $border, $focus-bg, $focus-border, $no-hover-bg
      @include applyTheme($color, #fff, transparent, darken($color, 5%), darken($color, 5%), $color);
    }

    &.is-fix-picture-transparency {
      background-color: #fff !important;
      span.picture {
        box-shadow: none;
      }
    }

  }
</style>



// WEBPACK FOOTER //
// src/components/ui/label/nuttrema-iconica-sidebar-link.vue